import { observer } from "mobx-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Center,
  CopyButton,
  Divider,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconCreditCard,
  IconDeviceFloppy,
  IconDownload,
  IconGift,
  IconMinus,
  IconPhoto,
  IconPlus,
  IconRefresh,
  IconSend,
  IconShoppingCart,
  IconWallet,
} from "@tabler/icons-react";

import { sendToBobController } from "../../helpers/requests";
import { BOB_MODALS } from "../helpers/constants";
import { BobGiftFromServer } from "../stores/Bob";
import { BobsWorkspace } from "../stores/BobsWorkspace";
import TwoFactorInput from "./details/twoFactorInput";

type GiftCardOption = {
  satoshisAmount: number;
  availableCount: number;
};

type GiftCardCart = Record<number, number>;

type GiftCardSizesResponse = {
  success: boolean;
  giftCardSizes?: GiftCardOption[];
  giftCardSizesSatoshis?: number[];
  fixedFeeSatoshisForBobGiftCard?: number;
  message?: string;
};

type GiftCardPurchaseResponse = GiftCardSizesResponse & {
  bob_gifts?: BobGiftFromServer[];
  platform_credits?: any[];
  safe_balance_in_satoshis?: number;
  in_flight_satoshis?: number;
};

type ClipboardItemConstructor = {
  new (items: Record<string, Blob | Promise<Blob>>): unknown;
  supports?: (type: string) => boolean;
};

type GiftCardImageVariant = "lossless" | "compressed";
type GiftCardImageRequestSource = "copy_image" | "download_image";

const giftCardImageEndpoint = "/bob_gift_card/image";
const giftCardPrivateNoteEndpoint = "/bob_gift_card/update_private_note";
const giftCardCopyImageType = "image/png";
const giftCardPrivateNoteMaxLength = 2000;

const glassPanel = {
  background: "rgba(20,20,20,.45)",
  border: "1px solid rgba(255,255,255,.08)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
} as const;

const satoshisToBtc = (satoshis: number) =>
  `${(satoshis / 100_000_000).toFixed(8)} BTC`;

const formatGiftCardDate = (timestamp: number | null) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };

  if (date.getFullYear() !== new Date().getFullYear()) {
    options.year = "numeric";
  }

  return date.toLocaleString(undefined, options);
};

const giftCardIsRedeemed = (giftCard: BobGiftFromServer) =>
  giftCard.redemption_status === "redeemed" || Boolean(giftCard.redeemed_at);

const buildGiftCardShareText = (
  giftCard: BobGiftFromServer,
  fiatAmount: string,
) =>
  `You've got a Bitcoin gift card! Go to Rizful.com, sign up, and use code ${giftCard.secret_code} to get ${giftCard.satoshis_amount.toLocaleString()} satoshis (${fiatAmount}). Note: Don't show this code to anyone. This code can be used only once and anyone can redeem it.`;

const giftCardPublicS3BaseUrl = "https://rizful-public.s3.amazonaws.com";
const giftCardCloudFrontBaseUrl = "https://daxwktpz4hkwd.cloudfront.net";
const giftCardWebDemoBaseUrl = `${giftCardCloudFrontBaseUrl}/gift_cards/web_demos`;

const getGiftCardImageFilenameFromUrl = (url: string | null) => {
  if (!url) return null;

  try {
    return new URL(url).pathname.split("/").filter(Boolean).pop() || null;
  } catch {
    return url.split(/[?#]/)[0].split("/").filter(Boolean).pop() || null;
  }
};

const getGiftCardImageFilename = (giftCard: BobGiftFromServer) =>
  getGiftCardImageFilenameFromUrl(giftCard.image_url_lossless) ||
  "rizful-bitcoin-gift-card.png";

const getGiftCardCloudFrontUrl = (url: string | null) => {
  if (!url) return null;

  return url.replace(giftCardPublicS3BaseUrl, giftCardCloudFrontBaseUrl);
};

const getGiftCardCompressedImageUrl = (giftCard: BobGiftFromServer) =>
  getGiftCardCloudFrontUrl(giftCard.image_url_compressed);

const getGiftCardWebDemoUrl = (satoshisAmount: number) => {
  const amountInThousands = satoshisAmount / 1_000;

  if (!Number.isInteger(amountInThousands)) return null;

  return `${giftCardWebDemoBaseUrl}/card-demo-${amountInThousands}k.webp`;
};

const fetchGiftCardImageBlob = async ({
  bobUuid,
  serialCode,
  variant,
  requestSource,
}: {
  bobUuid: string;
  serialCode: string;
  variant: GiftCardImageVariant;
  requestSource: GiftCardImageRequestSource;
}) => {
  const csrfToken =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || "";
  const expectedContentType =
    variant === "lossless" ? "image/png" : "image/webp";

  const response = await fetch(giftCardImageEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify({
      bobUuid,
      serialCode,
      variant,
      requestSource,
    }),
  });

  if (!response.ok) {
    throw new Error(`Could not fetch gift card image (${response.status}).`);
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.toLowerCase().startsWith(expectedContentType)) {
    throw new Error(`Unexpected gift card image content type: ${contentType}`);
  }

  const blob = await response.blob();
  return blob.type === expectedContentType
    ? blob
    : new Blob([blob], { type: expectedContentType });
};

const cleanFixedFeeSatoshis = (fixedFeeSatoshis: unknown) => {
  const cleanFee = Math.floor(Number(fixedFeeSatoshis));

  return Number.isFinite(cleanFee) && cleanFee > 0 ? cleanFee : 0;
};

const getGiftCardChargeSatoshis = (
  satoshisAmount: number,
  fixedFeeSatoshisForBobGiftCard: number,
) => satoshisAmount + fixedFeeSatoshisForBobGiftCard;

const getCartTotalSatoshis = (
  cart: GiftCardCart,
  fixedFeeSatoshisForBobGiftCard: number,
) =>
  Object.entries(cart).reduce(
    (total, [satoshisAmount, quantity]) =>
      total +
      getGiftCardChargeSatoshis(
        Number(satoshisAmount),
        fixedFeeSatoshisForBobGiftCard,
      ) *
        quantity,
    0,
  );

const areCartsEqual = (a: GiftCardCart, b: GiftCardCart) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  return aKeys.every((key) => a[Number(key)] === b[Number(key)]);
};

const cleanGiftCardOptions = (
  options: unknown,
  legacySizes: unknown,
): GiftCardOption[] => {
  const groupedOptions = new Map<number, number>();

  if (Array.isArray(options)) {
    options.forEach((option) => {
      const giftCardOption = option as Partial<GiftCardOption>;
      const satoshisAmount = Number(giftCardOption.satoshisAmount);
      const availableCount = Math.floor(Number(giftCardOption.availableCount));

      if (
        Number.isFinite(satoshisAmount) &&
        satoshisAmount > 0 &&
        Number.isFinite(availableCount) &&
        availableCount > 0
      ) {
        groupedOptions.set(
          satoshisAmount,
          (groupedOptions.get(satoshisAmount) || 0) + availableCount,
        );
      }
    });
  }

  if (groupedOptions.size === 0 && Array.isArray(legacySizes)) {
    legacySizes.forEach((size) => {
      const satoshisAmount = Number(size);

      if (Number.isFinite(satoshisAmount) && satoshisAmount > 0) {
        groupedOptions.set(satoshisAmount, 1);
      }
    });
  }

  return Array.from(groupedOptions, ([satoshisAmount, availableCount]) => ({
    satoshisAmount,
    availableCount,
  })).sort((a, b) => a.satoshisAmount - b.satoshisAmount);
};

const clampCartToOptions = (
  cart: GiftCardCart,
  giftCardOptions: GiftCardOption[],
  balanceSatoshis: number,
  fixedFeeSatoshisForBobGiftCard: number,
) => {
  const nextCart: GiftCardCart = {};
  let nextTotalSatoshis = 0;

  giftCardOptions.forEach(({ satoshisAmount, availableCount }) => {
    const currentQuantity = cart[satoshisAmount] || 0;
    const giftCardChargeSatoshis = getGiftCardChargeSatoshis(
      satoshisAmount,
      fixedFeeSatoshisForBobGiftCard,
    );
    const maxQuantityByBalance = Math.floor(
      Math.max(balanceSatoshis - nextTotalSatoshis, 0) / giftCardChargeSatoshis,
    );
    const nextQuantity = Math.min(
      currentQuantity,
      availableCount,
      maxQuantityByBalance,
    );

    if (nextQuantity > 0) {
      nextCart[satoshisAmount] = nextQuantity;
      nextTotalSatoshis += giftCardChargeSatoshis * nextQuantity;
    }
  });

  return nextCart;
};

const GiftCard = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const selectedBob = bobsWorkspace.selectedBob;
    const isMobile = useMediaQuery("(max-width: 640px)");
    const balanceSatoshis = selectedBob?.safeBalanceInSatoshis ?? 0;
    const cartSummaryRef = useRef<HTMLDivElement | null>(null);
    const [giftCardOptions, setGiftCardOptions] = useState<GiftCardOption[]>(
      [],
    );
    const [cartQuantities, setCartQuantities] = useState<GiftCardCart>({});
    const [fixedFeeSatoshisForBobGiftCard, setFixedFeeSatoshisForBobGiftCard] =
      useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [purchaseErrorMessage, setPurchaseErrorMessage] = useState<
      string | null
    >(null);
    const [twoFactorCode, setTwoFactorCode] = useState("");
    const [giftCardToShare, setGiftCardToShare] =
      useState<BobGiftFromServer | null>(null);
    const [shareModalErrorMessage, setShareModalErrorMessage] = useState<
      string | null
    >(null);
    const [isCopyingGiftCardImage, setIsCopyingGiftCardImage] = useState(false);
    const [giftCardImageWasCopied, setGiftCardImageWasCopied] = useState(false);
    const [isDownloadingGiftCardImage, setIsDownloadingGiftCardImage] =
      useState(false);
    const [privateNoteDraft, setPrivateNoteDraft] = useState("");
    const [isSavingPrivateNote, setIsSavingPrivateNote] = useState(false);
    const [privateNoteWasSaved, setPrivateNoteWasSaved] = useState(false);

    const purchasedGiftCards = selectedBob?.bobGifts.data ?? [];
    const readyGiftCards = useMemo(
      () =>
        purchasedGiftCards.filter((giftCard) => !giftCardIsRedeemed(giftCard)),
      [purchasedGiftCards],
    );
    const redeemedGiftCards = useMemo(
      () => purchasedGiftCards.filter(giftCardIsRedeemed),
      [purchasedGiftCards],
    );
    const activeView = selectedBob?.giftCardView ?? "shop";
    const shareGiftCard = useMemo(() => {
      if (!giftCardToShare) return null;

      return (
        purchasedGiftCards.find(
          (giftCard) => giftCard.serial_code === giftCardToShare.serial_code,
        ) || giftCardToShare
      );
    }, [giftCardToShare, purchasedGiftCards]);

    const applyGiftCardPayload = useCallback(
      (response: GiftCardPurchaseResponse) => {
        const cleanOptions = cleanGiftCardOptions(
          response.giftCardSizes,
          response.giftCardSizesSatoshis,
        );

        if (cleanOptions.length > 0 || Array.isArray(response.giftCardSizes)) {
          setGiftCardOptions(cleanOptions);
        }

        if (response.fixedFeeSatoshisForBobGiftCard !== undefined) {
          setFixedFeeSatoshisForBobGiftCard(
            cleanFixedFeeSatoshis(response.fixedFeeSatoshisForBobGiftCard),
          );
        }

        if (selectedBob && Array.isArray(response.bob_gifts)) {
          selectedBob.setBobGifts(response.bob_gifts);
        }

        if (selectedBob && Array.isArray(response.platform_credits)) {
          selectedBob.setPlatformCredits(response.platform_credits);
        }

        const safeBalance = Number(response.safe_balance_in_satoshis);
        const inFlightSatoshis = Number(response.in_flight_satoshis);
        if (
          selectedBob &&
          Number.isFinite(safeBalance) &&
          Number.isFinite(inFlightSatoshis)
        ) {
          selectedBob.updateBalance(safeBalance, inFlightSatoshis);
        }
      },
      [selectedBob],
    );

    const loadGiftCardSizes = useCallback(async () => {
      if (!selectedBob?.uuid) return;

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = (await sendToBobController(
          { bobUuid: selectedBob.uuid },
          "/bob_gift_card/gift_card_sizes",
        )) as GiftCardPurchaseResponse;

        if (!response.success) {
          setGiftCardOptions([]);
          setCartQuantities({});
          setFixedFeeSatoshisForBobGiftCard(0);
          setErrorMessage(response.message || "Could not load gift cards.");
          return;
        }

        const cleanOptions = cleanGiftCardOptions(
          response.giftCardSizes,
          response.giftCardSizesSatoshis,
        );
        setGiftCardOptions(cleanOptions);
        setFixedFeeSatoshisForBobGiftCard(
          cleanFixedFeeSatoshis(response.fixedFeeSatoshisForBobGiftCard),
        );
        applyGiftCardPayload(response);
      } catch (error) {
        console.log(error);
        setGiftCardOptions([]);
        setCartQuantities({});
        setFixedFeeSatoshisForBobGiftCard(0);
        setErrorMessage("Could not load gift cards.");
      } finally {
        setIsLoading(false);
      }
    }, [applyGiftCardPayload, selectedBob?.uuid]);

    useEffect(() => {
      setCartQuantities({});
      setFixedFeeSatoshisForBobGiftCard(0);
      setPurchaseErrorMessage(null);
      setTwoFactorCode("");
      selectedBob?.checkIfWeNeedToShowTwoFactorForm();
      loadGiftCardSizes();
    }, [loadGiftCardSizes, selectedBob]);

    useEffect(() => {
      setCartQuantities((currentCart) => {
        const nextCart = clampCartToOptions(
          currentCart,
          giftCardOptions,
          balanceSatoshis,
          fixedFeeSatoshisForBobGiftCard,
        );

        return areCartsEqual(currentCart, nextCart) ? currentCart : nextCart;
      });
    }, [balanceSatoshis, fixedFeeSatoshisForBobGiftCard, giftCardOptions]);

    useEffect(() => {
      setPrivateNoteDraft(shareGiftCard?.purchaser_private_note || "");
      setPrivateNoteWasSaved(false);
    }, [shareGiftCard?.serial_code, shareGiftCard?.purchaser_private_note]);

    const cartLines = useMemo(
      () =>
        giftCardOptions
          .map((option) => {
            const quantity = cartQuantities[option.satoshisAmount] || 0;
            const giftCardTotalSatoshis = option.satoshisAmount * quantity;
            const feeTotalSatoshis = fixedFeeSatoshisForBobGiftCard * quantity;

            return {
              ...option,
              quantity,
              giftCardTotalSatoshis,
              feeTotalSatoshis,
              lineTotalSatoshis: giftCardTotalSatoshis + feeTotalSatoshis,
            };
          })
          .filter((line) => line.quantity > 0),
      [cartQuantities, fixedFeeSatoshisForBobGiftCard, giftCardOptions],
    );

    const cartTotalSatoshis = useMemo(
      () =>
        cartLines.reduce((total, line) => total + line.lineTotalSatoshis, 0),
      [cartLines],
    );

    const cartGiftCardSubtotalSatoshis = useMemo(
      () =>
        cartLines.reduce(
          (total, line) => total + line.giftCardTotalSatoshis,
          0,
        ),
      [cartLines],
    );

    const cartFeeTotalSatoshis = useMemo(
      () => cartLines.reduce((total, line) => total + line.feeTotalSatoshis, 0),
      [cartLines],
    );

    const cartGiftCardCount = useMemo(
      () => cartLines.reduce((total, line) => total + line.quantity, 0),
      [cartLines],
    );

    const remainingBalanceSatoshis = Math.max(
      balanceSatoshis - cartTotalSatoshis,
      0,
    );

    const shareGiftCardImageUrl = shareGiftCard
      ? getGiftCardCompressedImageUrl(shareGiftCard)
      : null;
    const shareGiftCardHasLosslessImage = Boolean(
      shareGiftCard?.image_url_lossless,
    );
    const savedPrivateNote = shareGiftCard?.purchaser_private_note || "";
    const privateNoteHasChanges = privateNoteDraft !== savedPrivateNote;
    const shareGiftCardText = useMemo(() => {
      if (!shareGiftCard || !selectedBob) return "";

      return buildGiftCardShareText(
        shareGiftCard,
        selectedBob.formatFiat(shareGiftCard.satoshis_amount),
      );
    }, [shareGiftCard, selectedBob]);

    const addGiftCard = useCallback(
      (giftCardOption: GiftCardOption) => {
        setCartQuantities((currentCart) => {
          const currentQuantity =
            currentCart[giftCardOption.satoshisAmount] || 0;
          const currentTotalSatoshis = getCartTotalSatoshis(
            currentCart,
            fixedFeeSatoshisForBobGiftCard,
          );
          const giftCardChargeSatoshis = getGiftCardChargeSatoshis(
            giftCardOption.satoshisAmount,
            fixedFeeSatoshisForBobGiftCard,
          );

          if (currentQuantity >= giftCardOption.availableCount) {
            return currentCart;
          }

          if (currentTotalSatoshis + giftCardChargeSatoshis > balanceSatoshis) {
            return currentCart;
          }

          return {
            ...currentCart,
            [giftCardOption.satoshisAmount]: currentQuantity + 1,
          };
        });
      },
      [balanceSatoshis, fixedFeeSatoshisForBobGiftCard],
    );

    const removeGiftCard = useCallback((satoshisAmount: number) => {
      setCartQuantities((currentCart) => {
        const currentQuantity = currentCart[satoshisAmount] || 0;
        if (currentQuantity <= 0) return currentCart;

        const nextCart = { ...currentCart };
        const nextQuantity = currentQuantity - 1;

        if (nextQuantity > 0) {
          nextCart[satoshisAmount] = nextQuantity;
        } else {
          delete nextCart[satoshisAmount];
        }

        return nextCart;
      });
    }, []);

    const scrollToCart = useCallback(() => {
      cartSummaryRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, []);

    const openShareGiftCardModal = useCallback(
      (giftCard: BobGiftFromServer) => {
        setGiftCardToShare(giftCard);
        setPrivateNoteDraft(giftCard.purchaser_private_note || "");
        setShareModalErrorMessage(null);
        setGiftCardImageWasCopied(false);
        setPrivateNoteWasSaved(false);
      },
      [],
    );

    const closeShareGiftCardModal = useCallback(() => {
      setGiftCardToShare(null);
      setPrivateNoteDraft("");
      setShareModalErrorMessage(null);
      setGiftCardImageWasCopied(false);
      setPrivateNoteWasSaved(false);
    }, []);

    const copyGiftCardImageToClipboard = useCallback(async () => {
      const bobUuid = selectedBob?.uuid;

      if (!bobUuid || !shareGiftCard) {
        setShareModalErrorMessage(
          "Could not prepare this gift card image. Please refresh and try again.",
        );
        return;
      }

      if (!shareGiftCard.image_url_lossless) {
        setShareModalErrorMessage(
          "This gift card does not have a high-res image yet.",
        );
        return;
      }

      setIsCopyingGiftCardImage(true);
      setShareModalErrorMessage(null);
      setGiftCardImageWasCopied(false);

      try {
        const ClipboardItemClass = (
          window as Window & { ClipboardItem?: ClipboardItemConstructor }
        ).ClipboardItem;
        const clipboard = navigator.clipboard as
          | (Clipboard & { write?: (data: unknown[]) => Promise<void> })
          | undefined;

        if (!ClipboardItemClass || !clipboard?.write) {
          throw new Error("Image clipboard writes are not supported.");
        }

        if (ClipboardItemClass.supports?.(giftCardCopyImageType) === false) {
          throw new Error("PNG clipboard writes are not supported.");
        }

        const imageBlobPromise = fetchGiftCardImageBlob({
          bobUuid,
          serialCode: shareGiftCard.serial_code,
          variant: "lossless",
          requestSource: "copy_image",
        }).then((blob) =>
          blob.type === giftCardCopyImageType
            ? blob
            : new Blob([blob], { type: giftCardCopyImageType }),
        );

        await clipboard.write([
          new ClipboardItemClass({ [giftCardCopyImageType]: imageBlobPromise }),
        ]);
        setGiftCardImageWasCopied(true);
        window.setTimeout(() => setGiftCardImageWasCopied(false), 1600);
      } catch (error) {
        console.log(error);
        setShareModalErrorMessage(
          "Could not copy the image. You can still download it or copy the text.",
        );
      } finally {
        setIsCopyingGiftCardImage(false);
      }
    }, [selectedBob?.uuid, shareGiftCard]);

    const downloadGiftCardImage = useCallback(async () => {
      const bobUuid = selectedBob?.uuid;

      if (!bobUuid || !shareGiftCard) {
        setShareModalErrorMessage(
          "Could not prepare this gift card image. Please refresh and try again.",
        );
        return;
      }

      if (!shareGiftCard.image_url_lossless) {
        setShareModalErrorMessage(
          "This gift card does not have a high-res image yet.",
        );
        return;
      }

      const filename = getGiftCardImageFilename(shareGiftCard);

      setIsDownloadingGiftCardImage(true);
      setShareModalErrorMessage(null);

      try {
        const blob = await fetchGiftCardImageBlob({
          bobUuid,
          serialCode: shareGiftCard.serial_code,
          variant: "lossless",
          requestSource: "download_image",
        });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      } catch (error) {
        console.log(error);
        setShareModalErrorMessage(
          "Could not download the image. You can still copy the text.",
        );
      } finally {
        setIsDownloadingGiftCardImage(false);
      }
    }, [selectedBob?.uuid, shareGiftCard]);

    const updatePrivateNote = useCallback(async () => {
      if (!selectedBob?.uuid || !shareGiftCard || isSavingPrivateNote) return;

      if (privateNoteDraft.length > giftCardPrivateNoteMaxLength) {
        setShareModalErrorMessage(
          `Private note must be ${giftCardPrivateNoteMaxLength} characters or fewer.`,
        );
        return;
      }

      setIsSavingPrivateNote(true);
      setShareModalErrorMessage(null);
      setPrivateNoteWasSaved(false);

      try {
        const response = (await sendToBobController(
          {
            bobUuid: selectedBob.uuid,
            serialCode: shareGiftCard.serial_code,
            purchaserPrivateNote: privateNoteDraft,
          },
          giftCardPrivateNoteEndpoint,
        )) as GiftCardPurchaseResponse;

        applyGiftCardPayload(response);

        if (!response.success) {
          setShareModalErrorMessage(
            response.message || "Could not save the private note.",
          );
          return;
        }

        const updatedGiftCard = response.bob_gifts?.find(
          (giftCard) => giftCard.serial_code === shareGiftCard.serial_code,
        );

        if (updatedGiftCard) {
          setGiftCardToShare(updatedGiftCard);
          setPrivateNoteDraft(updatedGiftCard.purchaser_private_note || "");
        }

        setPrivateNoteWasSaved(true);
        window.setTimeout(() => setPrivateNoteWasSaved(false), 1600);
      } catch (error) {
        console.log(error);
        setShareModalErrorMessage("Could not save the private note.");
      } finally {
        setIsSavingPrivateNote(false);
      }
    }, [
      applyGiftCardPayload,
      isSavingPrivateNote,
      privateNoteDraft,
      selectedBob?.uuid,
      shareGiftCard,
    ]);

    const purchaseCart = useCallback(async () => {
      if (!selectedBob?.uuid || cartLines.length === 0 || isPurchasing) return;

      setIsPurchasing(true);
      setPurchaseErrorMessage(null);

      try {
        const response = (await sendToBobController(
          {
            bobUuid: selectedBob.uuid,
            cart: cartLines.map((line) => ({
              satoshisAmount: line.satoshisAmount,
              quantity: line.quantity,
            })),
            twoFactorCode: twoFactorCode.trim(),
          },
          "/bob_gift_card/purchase",
        )) as GiftCardPurchaseResponse;

        applyGiftCardPayload(response);

        if (!response.success) {
          setPurchaseErrorMessage(
            response.message || "Could not purchase gift cards.",
          );
          return;
        }

        setCartQuantities({});
        setTwoFactorCode("");
        selectedBob.checkIfWeNeedToShowTwoFactorForm();
        selectedBob.setGiftCardView("purchased");
      } catch (error) {
        console.log(error);
        setPurchaseErrorMessage("Could not purchase gift cards.");
      } finally {
        setIsPurchasing(false);
      }
    }, [
      applyGiftCardPayload,
      cartLines,
      isPurchasing,
      selectedBob?.uuid,
      twoFactorCode,
    ]);

    const twoFactorCodeIsReady =
      !bobsWorkspace.accountRequiresTwoFactorFormForActions ||
      /^\d{6}$/.test(twoFactorCode.trim());

    if (!selectedBob) return null;

    return (
      <>
        <Modal
          opened={Boolean(giftCardToShare)}
          onClose={closeShareGiftCardModal}
          title="Share gift card"
          size="lg"
          centered
        >
          {shareGiftCard && (
            <Stack gap="md">
              <Group justify="space-between" align="center" gap="sm">
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Bitcoin gift card
                  </Text>
                  <Text fw={900} fz="lg">
                    {shareGiftCard.satoshis_amount.toLocaleString()} sats
                  </Text>
                </Box>
                <Text size="sm" c="gray.4" ta="right">
                  {selectedBob.formatFiat(shareGiftCard.satoshis_amount)}
                </Text>
              </Group>

              {shareGiftCardImageUrl ? (
                <Box
                  style={{
                    width: "100%",
                    maxHeight: isMobile ? "58vh" : "68vh",
                    overflow: "hidden",
                    borderRadius: 8,
                    background: "rgba(0,0,0,.3)",
                  }}
                >
                  <Image
                    src={shareGiftCardImageUrl}
                    alt={`${shareGiftCard.satoshis_amount.toLocaleString()} satoshi Rizful gift card ${shareGiftCard.serial_code}`}
                    fit="contain"
                    radius={8}
                    style={{
                      width: "100%",
                      maxHeight: isMobile ? "58vh" : "68vh",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : (
                <Alert
                  icon={<IconPhoto size={18} />}
                  color="yellow"
                  variant="light"
                  title="Image unavailable"
                >
                  This gift card does not have a compressed image yet.
                </Alert>
              )}

              <Group grow={isMobile} gap="sm">
                <Button
                  variant={giftCardImageWasCopied ? "filled" : "light"}
                  color={giftCardImageWasCopied ? "green" : "blue"}
                  leftSection={
                    giftCardImageWasCopied ? (
                      <IconCheck size={16} />
                    ) : (
                      <IconCopy size={16} />
                    )
                  }
                  loading={isCopyingGiftCardImage}
                  disabled={!shareGiftCardHasLosslessImage}
                  onClick={copyGiftCardImageToClipboard}
                >
                  {giftCardImageWasCopied ? "Image copied" : "Copy image"}
                </Button>
                <Button
                  variant="light"
                  color="gray"
                  leftSection={<IconDownload size={16} />}
                  loading={isDownloadingGiftCardImage}
                  disabled={!shareGiftCardHasLosslessImage}
                  onClick={downloadGiftCardImage}
                >
                  Download
                </Button>
              </Group>

              {shareModalErrorMessage && (
                <Alert
                  icon={<IconAlertCircle size={18} />}
                  color="yellow"
                  variant="light"
                >
                  {shareModalErrorMessage}
                </Alert>
              )}

              <Stack gap={8}>
                <Group justify="space-between" align="center">
                  <Text size="sm" c="dimmed" fw={700}>
                    Message
                  </Text>
                  <CopyButton value={shareGiftCardText}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant="light"
                        color={copied ? "green" : "blue"}
                        leftSection={
                          copied ? (
                            <IconCheck size={14} />
                          ) : (
                            <IconCopy size={14} />
                          )
                        }
                        onClick={copy}
                      >
                        {copied ? "Copied" : "Copy text"}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
                <Textarea
                  value={shareGiftCardText}
                  readOnly
                  autosize
                  minRows={5}
                />
              </Stack>

              <Stack gap={8}>
                <Textarea
                  label="Add a private note"
                  description="Add a note about this gift card, for example to note who you sent it to. This note will not be revealed to your recipient."
                  value={privateNoteDraft}
                  autosize
                  minRows={3}
                  maxLength={giftCardPrivateNoteMaxLength}
                  onChange={(event) => {
                    setPrivateNoteDraft(event.currentTarget.value);
                    setPrivateNoteWasSaved(false);
                  }}
                />
                <Group justify="space-between" align="center" gap="sm">
                  <Text size="xs" c="dimmed">
                    {privateNoteDraft.length.toLocaleString()} /{" "}
                    {giftCardPrivateNoteMaxLength.toLocaleString()}
                  </Text>
                  <Button
                    size="xs"
                    variant={
                      privateNoteWasSaved && !privateNoteHasChanges
                        ? "filled"
                        : "light"
                    }
                    color={
                      privateNoteWasSaved && !privateNoteHasChanges
                        ? "green"
                        : "blue"
                    }
                    leftSection={
                      privateNoteWasSaved && !privateNoteHasChanges ? (
                        <IconCheck size={14} />
                      ) : (
                        <IconDeviceFloppy size={14} />
                      )
                    }
                    loading={isSavingPrivateNote}
                    disabled={
                      !privateNoteHasChanges ||
                      privateNoteDraft.length > giftCardPrivateNoteMaxLength
                    }
                    onClick={updatePrivateNote}
                  >
                    {privateNoteWasSaved && !privateNoteHasChanges
                      ? "Saved"
                      : "Update note"}
                  </Button>
                </Group>
              </Stack>
            </Stack>
          )}
        </Modal>

        <Stack gap="lg" px="md" pt="md">
          <Group
            justify="space-between"
            align={isMobile ? "stretch" : "center"}
            gap="sm"
            style={{ flexDirection: isMobile ? "column" : "row" }}
          >
            <Group gap="xs" justify={isMobile ? "flex-start" : undefined}>
              <IconCreditCard size={28} />
              <Title order={3}>Gift Card</Title>
            </Group>
            <Button
              variant="light"
              color="blue"
              leftSection={<IconGift size={16} />}
              fullWidth={isMobile}
              onClick={() => selectedBob.setOpenModal(BOB_MODALS.PROMO_CODE)}
            >
              Redeem Gift Card
            </Button>
          </Group>

          <SegmentedControl
            value={activeView}
            onChange={(value) =>
              selectedBob.setGiftCardView(value as "shop" | "purchased")
            }
            data={[
              { label: "Shop", value: "shop" },
              {
                label: `Purchased (${purchasedGiftCards.length.toLocaleString()})`,
                value: "purchased",
              },
            ]}
            fullWidth
          />

          <Paper p="md" radius="md" style={glassPanel}>
            <Group justify="space-between" align="center">
              <Group gap="sm">
                <ThemeIcon variant="light" color="blue" size="lg" radius="md">
                  <IconWallet size={22} />
                </ThemeIcon>
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                    Available balance
                  </Text>
                  <Text fw={800} fz="lg">
                    {balanceSatoshis.toLocaleString()} sats
                  </Text>
                </Box>
              </Group>
              <Stack gap={0} align="flex-end">
                <Text c="gray.4" fw={700}>
                  {selectedBob.formatFiat(balanceSatoshis)}
                </Text>
                {activeView === "shop" && cartTotalSatoshis > 0 && (
                  <Text size="xs" c="dimmed" fw={700}>
                    {remainingBalanceSatoshis.toLocaleString()} sats left
                  </Text>
                )}
              </Stack>
            </Group>
          </Paper>

          {activeView === "shop" && cartLines.length > 0 && (
            <Paper
              p="sm"
              radius="md"
              style={{
                ...glassPanel,
                borderColor: "rgba(77, 171, 247, .45)",
              }}
            >
              <Group
                justify="space-between"
                align="center"
                gap="sm"
                wrap="wrap"
              >
                <Group gap="sm" align="center">
                  <ThemeIcon
                    variant="light"
                    color="green"
                    size="lg"
                    radius="md"
                  >
                    <IconShoppingCart size={22} />
                  </ThemeIcon>
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Cart
                    </Text>
                    <Text fw={800}>
                      {cartGiftCardCount.toLocaleString()} card
                      {cartGiftCardCount === 1 ? "" : "s"} in cart
                    </Text>
                  </Box>
                </Group>

                <Button
                  size="xs"
                  variant="light"
                  color="blue"
                  leftSection={<IconShoppingCart size={14} />}
                  onClick={scrollToCart}
                >
                  View cart
                </Button>
              </Group>
            </Paper>
          )}

          {activeView === "shop" && isLoading && (
            <Center py="xl">
              <Stack align="center" gap="sm">
                <Loader size="sm" />
                <Text c="dimmed" size="sm">
                  Loading gift cards
                </Text>
              </Stack>
            </Center>
          )}

          {activeView === "shop" && !isLoading && errorMessage && (
            <Alert
              icon={<IconAlertCircle size={18} />}
              color="red"
              variant="light"
              title="Gift cards unavailable"
            >
              <Group justify="space-between" align="center" gap="md">
                <Text size="sm">{errorMessage}</Text>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  leftSection={<IconRefresh size={14} />}
                  onClick={loadGiftCardSizes}
                >
                  Retry
                </Button>
              </Group>
            </Alert>
          )}

          {activeView === "shop" &&
            !isLoading &&
            !errorMessage &&
            giftCardOptions.length === 0 && (
              <Paper p="lg" radius="md" style={glassPanel}>
                <Center>
                  <Stack align="center" gap="xs">
                    <ThemeIcon
                      variant="light"
                      color="blue"
                      size="xl"
                      radius="md"
                    >
                      <IconCreditCard size={28} />
                    </ThemeIcon>
                    <Text fw={700}>No gift card sizes available.</Text>
                  </Stack>
                </Center>
              </Paper>
            )}

          {activeView === "shop" &&
            !isLoading &&
            !errorMessage &&
            giftCardOptions.length > 0 && (
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                {giftCardOptions.map((giftCardOption) => {
                  const { satoshisAmount, availableCount } = giftCardOption;
                  const giftCardWebDemoUrl =
                    getGiftCardWebDemoUrl(satoshisAmount);
                  const quantity = cartQuantities[satoshisAmount] || 0;
                  const giftCardChargeSatoshis = getGiftCardChargeSatoshis(
                    satoshisAmount,
                    fixedFeeSatoshisForBobGiftCard,
                  );
                  const canEverPurchase =
                    availableCount > 0 &&
                    giftCardChargeSatoshis <= balanceSatoshis;
                  const wouldExceedBalance =
                    cartTotalSatoshis + giftCardChargeSatoshis >
                    balanceSatoshis;
                  const atInventoryLimit =
                    availableCount > 0 && quantity >= availableCount;
                  const canAdd =
                    canEverPurchase && !wouldExceedBalance && !atInventoryLimit;

                  return (
                    <Paper
                      key={satoshisAmount}
                      p="md"
                      radius="md"
                      style={{
                        ...glassPanel,
                        width: "100%",
                        minHeight: 172,
                        opacity: canEverPurchase ? 1 : 0.5,
                        borderColor:
                          quantity > 0
                            ? "rgba(77, 171, 247, .9)"
                            : "rgba(255,255,255,.08)",
                        boxShadow:
                          quantity > 0
                            ? "0 0 0 1px rgba(77, 171, 247, .45), 0 12px 34px rgba(77, 171, 247, .16)"
                            : "none",
                        transition:
                          "border-color 140ms ease, box-shadow 140ms ease, opacity 140ms ease",
                      }}
                    >
                      <Stack gap="sm">
                        {giftCardWebDemoUrl && (
                          <Box
                            style={{
                              width: "100%",
                              aspectRatio: "3 / 2",
                              overflow: "hidden",
                              borderRadius: 8,
                              background: "rgba(0,0,0,.25)",
                            }}
                          >
                            <Image
                              src={giftCardWebDemoUrl}
                              alt={`${satoshisAmount.toLocaleString()} satoshi Rizful gift card`}
                              fit="cover"
                              radius={8}
                              style={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                              }}
                            />
                          </Box>
                        )}

                        <Group justify="space-between" align="center">
                          <Text
                            size="sm"
                            c={quantity > 0 ? "blue.2" : "dimmed"}
                          >
                            In cart: {quantity.toLocaleString()}
                          </Text>
                          <Group gap="xs">
                            <ActionIcon
                              variant="light"
                              color="gray"
                              size="lg"
                              radius="md"
                              aria-label={`Remove ${satoshisAmount.toLocaleString()} sat gift card`}
                              disabled={quantity === 0}
                              onClick={() => removeGiftCard(satoshisAmount)}
                            >
                              <IconMinus size={18} />
                            </ActionIcon>
                            <ActionIcon
                              variant="filled"
                              color="blue"
                              size="lg"
                              radius="md"
                              aria-label={`Add ${satoshisAmount.toLocaleString()} sat gift card`}
                              disabled={!canAdd}
                              onClick={() => addGiftCard(giftCardOption)}
                            >
                              <IconPlus size={18} />
                            </ActionIcon>
                          </Group>
                        </Group>
                      </Stack>
                    </Paper>
                  );
                })}
              </SimpleGrid>
            )}

          {activeView === "shop" && cartLines.length > 0 && (
            <Paper
              ref={cartSummaryRef}
              p="md"
              radius="md"
              style={{ ...glassPanel, scrollMarginTop: 16 }}
            >
              <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Group gap="sm">
                    <ThemeIcon
                      variant="light"
                      color="green"
                      size="lg"
                      radius="md"
                    >
                      <IconShoppingCart size={22} />
                    </ThemeIcon>
                    <Box>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                        Cart
                      </Text>
                      <Text fw={800}>
                        {cartGiftCardCount.toLocaleString()} gift card
                        {cartGiftCardCount === 1 ? "" : "s"}
                      </Text>
                    </Box>
                  </Group>
                  <Stack gap={0} align="flex-end">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Total due
                    </Text>
                    <Text fw={900}>
                      {cartTotalSatoshis.toLocaleString()} sats
                    </Text>
                    <Text size="sm" c="gray.4">
                      {selectedBob.formatFiat(cartTotalSatoshis)}
                    </Text>
                    <Button
                      mt="xs"
                      size="sm"
                      leftSection={<IconCreditCard size={16} />}
                      loading={isPurchasing}
                      disabled={!twoFactorCodeIsReady || cartLines.length === 0}
                      onClick={purchaseCart}
                    >
                      Purchase
                    </Button>
                  </Stack>
                </Group>

                {purchaseErrorMessage && (
                  <Alert
                    icon={<IconAlertCircle size={18} />}
                    color="red"
                    variant="light"
                    title="Purchase failed"
                  >
                    {purchaseErrorMessage}
                  </Alert>
                )}

                <TwoFactorInput
                  visible={bobsWorkspace.accountRequiresTwoFactorFormForActions}
                  code={twoFactorCode}
                  onCodeChange={setTwoFactorCode}
                />

                <Divider />

                <Stack gap={8}>
                  {cartLines.map((line) => (
                    <Group
                      key={line.satoshisAmount}
                      justify="space-between"
                      gap="md"
                    >
                      <Text size="sm" fw={700}>
                        {line.quantity.toLocaleString()} x{" "}
                        {line.satoshisAmount.toLocaleString()} sats
                      </Text>
                      <Stack gap={0} align="flex-end">
                        <Text size="sm" fw={800}>
                          {line.lineTotalSatoshis.toLocaleString()} sats
                        </Text>
                        <Text size="xs" c="dimmed">
                          {satoshisToBtc(line.lineTotalSatoshis)}
                        </Text>
                      </Stack>
                    </Group>
                  ))}
                </Stack>

                <Divider />

                {cartFeeTotalSatoshis > 0 && (
                  <>
                    <Stack gap={6}>
                      <Group justify="space-between" align="center">
                        <Text size="sm" c="dimmed" fw={700}>
                          Gift card value
                        </Text>
                        <Text size="sm" fw={800}>
                          {cartGiftCardSubtotalSatoshis.toLocaleString()} sats
                        </Text>
                      </Group>

                      <Group justify="space-between" align="center">
                        <Text size="sm" c="dimmed" fw={700}>
                          Fees
                        </Text>
                        <Text size="sm" fw={800}>
                          {cartFeeTotalSatoshis.toLocaleString()} sats
                        </Text>
                      </Group>

                      <Group justify="space-between" align="center">
                        <Text size="sm" c="gray.3" fw={800}>
                          Total due
                        </Text>
                        <Text size="sm" fw={900}>
                          {cartTotalSatoshis.toLocaleString()} sats
                        </Text>
                      </Group>
                    </Stack>

                    <Divider />
                  </>
                )}

                <Group justify="space-between" align="center">
                  <Text size="sm" c="dimmed" fw={700}>
                    Remaining balance
                  </Text>
                  <Text size="sm" fw={800}>
                    {remainingBalanceSatoshis.toLocaleString()} sats
                  </Text>
                </Group>
              </Stack>
            </Paper>
          )}

          {activeView === "purchased" && purchasedGiftCards.length === 0 && (
            <Paper p="lg" radius="md" style={glassPanel}>
              <Center>
                <Stack align="center" gap="xs">
                  <ThemeIcon variant="light" color="blue" size="xl" radius="md">
                    <IconGift size={28} />
                  </ThemeIcon>
                  <Text fw={700}>No purchased gift cards yet.</Text>
                </Stack>
              </Center>
            </Paper>
          )}

          {activeView === "purchased" && purchasedGiftCards.length > 0 && (
            <Stack gap="sm">
              {readyGiftCards.length > 0 && (
                <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                  {readyGiftCards.map((giftCard) => {
                    const imageUrl = getGiftCardCompressedImageUrl(giftCard);
                    const purchasedAt = formatGiftCardDate(
                      giftCard.purchased_at,
                    );
                    const privateNote = giftCard.purchaser_private_note?.trim();

                    return (
                      <Paper
                        key={giftCard.serial_code}
                        p="md"
                        radius="md"
                        style={{
                          ...glassPanel,
                          width: "100%",
                          borderColor: "rgba(77, 171, 247, .45)",
                        }}
                      >
                        <Stack gap="sm">
                          {imageUrl && (
                            <Box
                              style={{
                                width: "100%",
                                aspectRatio: "3 / 2",
                                overflow: "hidden",
                                borderRadius: 8,
                                background: "rgba(0,0,0,.25)",
                              }}
                            >
                              <Image
                                src={imageUrl}
                                alt={`${giftCard.satoshis_amount.toLocaleString()} satoshi Rizful gift card ${giftCard.serial_code}`}
                                fit="cover"
                                radius={8}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "block",
                                }}
                              />
                            </Box>
                          )}

                          <Group
                            justify="space-between"
                            align="flex-start"
                            gap="md"
                            wrap="nowrap"
                          >
                            <Box style={{ minWidth: 0 }}>
                              <Text
                                size="xs"
                                c="dimmed"
                                tt="uppercase"
                                fw={700}
                              >
                                Gift card
                              </Text>
                              <Text fw={900} fz="lg">
                                {giftCard.satoshis_amount.toLocaleString()} sats
                              </Text>
                              <Text size="sm" c="gray.4">
                                {selectedBob.formatFiat(
                                  giftCard.satoshis_amount,
                                )}
                              </Text>
                            </Box>
                            <Stack
                              gap={3}
                              align="flex-end"
                              style={{
                                flexShrink: 0,
                                maxWidth: "52%",
                                textAlign: "right",
                              }}
                            >
                              <Badge color="blue" variant="light">
                                Ready
                              </Badge>
                              <Text
                                size="xs"
                                c="gray.4"
                                fw={700}
                                style={{
                                  fontFamily: "monospace",
                                  lineHeight: 1.15,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {giftCard.serial_code}
                              </Text>
                              {purchasedAt && (
                                <Text
                                  size="xs"
                                  c="dimmed"
                                  style={{ lineHeight: 1.15 }}
                                >
                                  {purchasedAt}
                                </Text>
                              )}
                            </Stack>
                          </Group>

                          {privateNote && (
                            <Text
                              size="sm"
                              c="dimmed"
                              lineClamp={3}
                              style={{
                                whiteSpace: "pre-wrap",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {privateNote}
                            </Text>
                          )}

                          <Button
                            variant="light"
                            color="blue"
                            leftSection={<IconSend size={16} />}
                            fullWidth
                            onClick={() => openShareGiftCardModal(giftCard)}
                          >
                            Share card
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              )}

              {redeemedGiftCards.length > 0 && (
                <Paper
                  p="sm"
                  radius="md"
                  style={{
                    ...glassPanel,
                    borderColor: "rgba(64, 192, 87, .45)",
                  }}
                >
                  <Stack gap="xs">
                    <Group justify="space-between" align="center" gap="sm">
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                        Redeemed
                      </Text>
                      <Badge color="green" variant="light">
                        {redeemedGiftCards.length.toLocaleString()}
                      </Badge>
                    </Group>

                    <Box style={{ overflowX: "auto" }}>
                      <Table
                        verticalSpacing={6}
                        horizontalSpacing="sm"
                        style={{ minWidth: 560 }}
                      >
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Amount</Table.Th>
                            <Table.Th>Serial</Table.Th>
                            <Table.Th>Purchased</Table.Th>
                            <Table.Th>Redeemed</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {redeemedGiftCards.map((giftCard) => {
                            const purchasedAt =
                              formatGiftCardDate(giftCard.purchased_at) || "-";
                            const redeemedAt =
                              formatGiftCardDate(giftCard.redeemed_at) || "-";

                            return (
                              <Table.Tr key={giftCard.serial_code}>
                                <Table.Td>
                                  <Stack gap={0}>
                                    <Text size="sm" fw={800}>
                                      {giftCard.satoshis_amount.toLocaleString()}{" "}
                                      sats
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {selectedBob.formatFiat(
                                        giftCard.satoshis_amount,
                                      )}
                                    </Text>
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Text
                                    size="xs"
                                    c="gray.4"
                                    fw={700}
                                    style={{
                                      fontFamily: "monospace",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {giftCard.serial_code}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs" c="dimmed">
                                    {purchasedAt}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="xs" c="green.2" fw={700}>
                                    {redeemedAt}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </Stack>
      </>
    );
  },
);

export default GiftCard;
